import { Folder } from '@/api/generatedApi';
import { useUpdateFolderDescription } from '@/hooks/api/folders/useUpdateFolderDescription';
import { AssetsPageObjectModel } from '@/poms/assetsPageObjectModel';
import { Box, InputAdornment, TextField, Typography } from '@mui/material';
import { useState } from 'react';

export const ToolbarDescription = ({ folder, canEdit }: { folder: Folder; canEdit: boolean }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');

  const { updateDescription } = useUpdateFolderDescription({ folder });

  const displayValue = folder.description ?? '';

  const handleClick = () => {
    if (!canEdit) return;
    setEditValue(displayValue);
    setIsEditing(true);
  };

  const handleSave = () => {
    const trimmed = editValue.trim();
    if (trimmed === displayValue) {
      setIsEditing(false);
      return;
    }
    updateDescription(trimmed);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSave();
    }
    if (event.key === 'Escape') {
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <Box sx={{ px: 2, pt: 0, pb: 0.5, minHeight: 38, display: 'flex', alignItems: 'center' }}>
        <TextField
          autoFocus
          fullWidth
          size='small'
          multiline
          maxRows={3}
          value={editValue}
          onChange={event => setEditValue(event.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleSave}
          slotProps={{
            htmlInput: { maxLength: 255 },
            input: {
              endAdornment: (
                <InputAdornment position='end' sx={{ alignSelf: 'flex-end', mb: '4px' }}>
                  <Typography variant='caption' color='text.secondary' sx={{ whiteSpace: 'nowrap' }}>
                    {editValue.length}/255
                  </Typography>
                </InputAdornment>
              ),
            },
          }}
          placeholder='Enter a description...'
          sx={{
            '& .MuiInputBase-root': { py: '4px' },
            '& .MuiInputBase-input': { fontSize: '0.875rem' },
          }}
        />
      </Box>
    );
  }

  if (!displayValue && !canEdit) return;

  return (
    <Box
      data-testid={AssetsPageObjectModel.ToolbarFolderDescription}
      onClick={handleClick}
      sx={{
        px: 2,
        pt: 0,
        pb: 0.5,
        minHeight: 38,
        display: 'flex',
        alignItems: 'center',
        cursor: canEdit ? 'pointer' : 'default',
        borderRadius: '4px',
        '&:hover': canEdit ? { '& .description-text': { textDecoration: 'underline' } } : {},
      }}
    >
      {displayValue ? (
        <Typography className='description-text' variant='body2' color='text.secondary' sx={{ mt: '-2px' }}>
          {displayValue}
        </Typography>
      ) : (
        canEdit && (
          <Typography variant='body2' sx={{ mt: '-2px', color: 'text.secondary', opacity: 0.5, fontStyle: 'italic' }}>
            Click to add a description...
          </Typography>
        )
      )}
    </Box>
  );
};
